import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Ban, CheckCircle2, RotateCcw, TriangleAlert, Undo2 } from "lucide-react";
import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";

import type { ReceiptKind, TransferSessionHeader } from "@/api/transfer_check";
import {
  type AcceptResult,
  cancelDiscrepancy,
  getDiscrepancy,
  isPostRefusal,
  recountDiscrepancy,
  resetReceipt,
  resolveDiscrepancyByReceipt,
} from "@/api/transfer_receipts";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { extractErrorMessage } from "@/lib/api-errors";
import { Dialog } from "@/lib/scan-ui/overlays";
import { AccessDenied, LoadState, isForbidden, useGoBack } from "@/lib/scan-ui/screen";
import { fmtDateTime } from "@/lib/format";
import { CompareTable, TransferFacts, hasDiff, receiptTitle } from "@/pages/transfer-receipts/shared";

type Action = "recount" | "by_receipt" | "cancel" | "reset";

type ConfirmView = { title: string; body: string; label: string; danger?: boolean };

/** Подтверждение действия: что произойдёт с перемещением и с 1С — по виду.
 *  У «Перемещения» без заказа (`free`) заказа нет: ни подгонки, ни отката, и
 *  собирают заново в «Перемещении», а не новой проверкой. */
const CONFIRM: Record<ReceiptKind, Record<Action, ConfirmView>> = {
  order: {
    recount: {
      title: "Вернуть получателю на пересчёт?",
      body: "Приёмка снова откроется у получателя: его строки сохранятся, их можно поправить и нажать «Принять» ещё раз. В 1С ничего не запишется.",
      label: "Пересчитать",
    },
    by_receipt: {
      title: "Провести по принятому?",
      body: "Заказ подгонится под ПРИНЯТОЕ получателем, портал создаст и проведёт «Перемещение запасов» в 1С по его строкам.",
      label: "Провести",
    },
    cancel: {
      title: "Отменить перемещение?",
      body: "В 1С ничего не запишется. Заказ можно будет собрать заново новой проверкой.",
      label: "Отменить перемещение",
      danger: true,
    },
    reset: {
      title: "Сбросить приёмку?",
      body: "Документ и правка заказа в 1С будут отменены, получатель примет заново. Его строки сохранятся — их можно поправить.",
      label: "Сбросить приёмку",
      danger: true,
    },
  },
  free: {
    recount: {
      title: "Вернуть получателю на пересчёт?",
      body: "Приёмка снова откроется у получателя: его строки сохранятся, их можно поправить и нажать «Принять» ещё раз. В 1С ничего не запишется.",
      label: "Пересчитать",
    },
    by_receipt: {
      title: "Провести по принятому?",
      body: "Портал создаст и проведёт «Перемещение запасов» в 1С по строкам получателя.",
      label: "Провести",
    },
    cancel: {
      title: "Отменить перемещение?",
      body: "В 1С не останется ничего; перемещение можно собрать заново в «Перемещении».",
      label: "Отменить перемещение",
      danger: true,
    },
    reset: {
      title: "Сбросить приёмку?",
      body: "Документ в 1С будет отменён, получатель примет заново. Его строки сохранятся — их можно поправить.",
      label: "Сбросить приёмку",
      danger: true,
    },
  },
};

/** Тексты панелей и итогов, где у видов расходятся слова. */
const KIND_TEXT: Record<
  ReceiptKind,
  { notDone: string; resetWhat: string; afterCancel: string; byReceiptDone: string }
> = {
  order: {
    notDone: "Заказ и документ в 1С ещё не доведены.",
    resetWhat: "документ и правка заказа в 1С будут отменены",
    afterCancel: "Заказ можно собрать заново новой проверкой.",
    byReceiptDone: "Заказ подогнан под принятое получателем.",
  },
  free: {
    notDone: "Документ в 1С ещё не создан.",
    resetWhat: "документ в 1С будет отменён",
    afterCancel: "Перемещение можно собрать заново в «Перемещении».",
    byReceiptDone: "Документ — по строкам получателя.",
  },
};

/** Текст подтверждения: «Отменить» у недоведённого — откат записанного в 1С,
 *  а не «ничего не запишется». */
function confirmView(a: Action, h: TransferSessionHeader): ConfirmView {
  const views = CONFIRM[h.kind];
  if (a === "cancel" && (h.pending_resolution || h.transfer_doc_number))
    return {
      ...views.cancel,
      body:
        h.kind === "free"
          ? "Документ, уже записанный по перемещению в 1С, будет отменён — в 1С не останется ничего. Перемещение можно собрать заново в «Перемещении»."
          : "Всё, что по перемещению уже записано в 1С (документ, правка заказа), будет отменено — в 1С не останется ничего. Заказ можно будет собрать заново новой проверкой.",
    };
  return views[a];
}

type Outcome =
  | { kind: "recount"; header: TransferSessionHeader }
  | { kind: "cancel"; header: TransferSessionHeader }
  | { kind: "reset"; header: TransferSessionHeader }
  | { kind: "by_receipt"; result: AcceptResult };

/** Что можно делать с сессией на этом экране — по статусу, решению и
 *  доведённости до 1С (по ссылке сюда попадают и уже разобранные).
 *  `stuck_match` — «Принять» записано, а 1С не доведена (документа нет или он
 *  не проведён): получатель повтором не всегда доделает, разбирающий
 *  сбрасывает приёмку или отменяет перемещение. */
export type DiscrepancyMode = "decide" | "finish_by_receipt" | "stuck_match" | "resolved";

function notDone(h: TransferSessionHeader): boolean {
  return h.pending_resolution || !(h.status === "completed" && h.transfer_doc_posted);
}

export function discrepancyMode(h: TransferSessionHeader): DiscrepancyMode {
  // «Провести по принятому» сохранено, а документ не доведён — доделать.
  if (h.resolution === "by_receipt" && notDone(h)) return "finish_by_receipt";
  if (h.resolution === "match" && notDone(h) && (h.status === "sent" || h.status === "completed"))
    return "stuck_match";
  if (h.status === "discrepancy") return "decide";
  return "resolved";
}

/** Разобранное: что с перемещением сейчас — текстом, без действий. */
function resolvedText(h: TransferSessionHeader): { title: string; text: string } {
  if (h.status === "cancelled") return { title: "Перемещение отменено", text: "В 1С ничего не записано." };
  if (h.status === "completed")
    return h.resolution === "by_receipt"
      ? { title: "Проведено по принятому", text: `Перемещение № ${h.transfer_doc_number ?? "—"} проведено по строкам получателя.` }
      : {
          title: "Принято без расхождений",
          text: h.transfer_doc_number
            ? `Перемещение № ${h.transfer_doc_number}${h.transfer_doc_posted ? " проведено" : " — не проведено"}.`
            : "Документа в 1С нет.",
        };
  if (h.status === "sent" && h.resolution === "recount")
    return { title: "Возвращено получателю на пересчёт", text: `Приёмка снова во «Входящих» склада «${h.to_name || "—"}».` };
  if (h.status === "sent")
    return { title: "Приёмка у получателя", text: `Перемещение во «Входящих» склада «${h.to_name || "—"}» — расхождения нет.` };
  if (h.status === "open")
    return { title: "Возвращено в работу отправителю", text: "Расхождения больше нет — перемещение снова собирают." };
  return { title: `Статус: ${h.status}`, text: "Разбирать нечего." };
}

/**
 * Расхождение по перемещению: сравнение (строки с разницей подсвечены) и три
 * решения с подтверждением — только пока статус `discrepancy`. Недоведённое
 * «провести по принятому» — повтор и «Отменить»; застрявшее «Принять» —
 * «Сбросить приёмку» и «Отменить» (откат записанного в 1С); разобранное —
 * статус, без действий. Итог действия держится на экране сам; после любого
 * действия перечитываются и список, и деталь, и приёмка. 409 «уже разобрано»
 * и прочие отказы — текстом сервера; 502 (отказ 1С посреди проведения или
 * отката) — текст и «Повторить».
 */
export function TransferDiscrepancyPage(): React.ReactElement {
  const { sessionId } = useParams<{ sessionId: string }>();
  const id = sessionId!;
  const qc = useQueryClient();
  const goBack = useGoBack("/transfer-discrepancies");
  const q = useQuery({ queryKey: ["transfer-discrepancy", id], queryFn: () => getDiscrepancy(id) });
  const [confirm, setConfirm] = useState<Action | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [error, setError] = useState<{ text: string; retry: Action | null } | null>(null);
  // Синхронный замок: двойной тап — один запрос; успех на экране поздний
  // 409 гонки не перетирает.
  const busyRef = useRef(false);
  const doneRef = useRef(false);

  function invalidateAll() {
    void qc.invalidateQueries({ queryKey: ["transfer-discrepancies"] });
    void qc.invalidateQueries({ queryKey: ["transfer-discrepancy", id] });
    void qc.invalidateQueries({ queryKey: ["transfer-receipts"] });
    void qc.invalidateQueries({ queryKey: ["transfer-receipt", id] });
  }

  const actM = useMutation({
    mutationFn: async (a: Action): Promise<Outcome> => {
      if (a === "recount") return { kind: "recount", header: await recountDiscrepancy(id) };
      if (a === "cancel") return { kind: "cancel", header: await cancelDiscrepancy(id) };
      if (a === "reset") return { kind: "reset", header: await resetReceipt(id) };
      return { kind: "by_receipt", result: await resolveDiscrepancyByReceipt(id) };
    },
    onSuccess: (o) => {
      setConfirm(null);
      setError(null);
      setOutcome(o);
      // Проведено — итог окончательный; «создано, не проведено» ждёт повтора,
      // и его 409/502 показывать нужно.
      doneRef.current = o.kind !== "by_receipt" || o.result.posted;
      invalidateAll();
    },
    onError: (e, a) => {
      setConfirm(null);
      invalidateAll();
      if (doneRef.current) return;
      setError({
        text: extractErrorMessage(e, "Не удалось выполнить действие"),
        // 502 — 1С отказала посреди шага (проведение или откат): сделанное
        // отмечено, повтор того же действия доделывает.
        retry: isPostRefusal(e) ? a : null,
      });
    },
    onSettled: () => {
      busyRef.current = false;
    },
  });
  function run(a: Action) {
    if (busyRef.current) return;
    busyRef.current = true;
    actM.mutate(a);
  }

  const d = q.data;
  const header = d?.header;
  const mode = header ? discrepancyMode(header) : null;
  const title =
    mode === "decide"
      ? "Расхождение"
      : mode === "finish_by_receipt"
        ? "Ждёт проведения"
        : mode === "stuck_match"
          ? "Принятие не доведено"
          : "Перемещение";
  const cancelButton = (
    <Button
      variant="outline"
      className="h-12 w-full border-red-300 text-red-700"
      disabled={actM.isPending}
      onClick={() => setConfirm("cancel")}
    >
      <Ban className="size-4" /> Отменить
    </Button>
  );

  return (
    <div className="mx-auto max-w-2xl space-y-3">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" aria-label="Назад" onClick={goBack}>
          <ArrowLeft className="size-5" />
        </Button>
        {/* Переносом, не многоточием: «Расхождение · Перемещение №12» на 360 px
            не помещается, а номер — то, ради чего заголовок. */}
        <h1 className="min-w-0 text-xl font-semibold [overflow-wrap:anywhere]">
          {header ? `${title} · ${receiptTitle(header)}` : "Расхождение"}
        </h1>
      </div>

      {!d ? (
        q.isError && isForbidden(q.error) ? (
          <AccessDenied error={q.error} className="flex flex-col items-center gap-3 p-6 text-center" />
        ) : (
          <LoadState isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()} className="flex flex-col items-center gap-3 p-6 text-center" />
        )
      ) : (
        <>
          <Card className="space-y-1 p-3">
            <TransferFacts header={d.header} className="text-sm" />
            {d.header.received_at && (
              <div className="text-xs text-muted-foreground">Принято {fmtDateTime(d.header.received_at)}</div>
            )}
          </Card>

          {outcome && (
            <OutcomePanel
              outcome={outcome}
              kind={d.header.kind}
              retrying={actM.isPending}
              onRetry={() => run("by_receipt")}
            />
          )}

          {error && (
            <div data-testid="discrepancy-error" className="rounded-lg border-2 border-red-300 bg-red-50 p-3 text-sm text-red-900">
              <div className="flex items-center gap-2 font-bold text-red-700">
                <TriangleAlert className="size-4" /> Не выполнено
              </div>
              <div className="mt-1 whitespace-pre-line">{error.text}</div>
              {error.retry && (
                <Button
                  className="mt-2 bg-red-700 hover:bg-red-800"
                  disabled={actM.isPending}
                  onClick={() => run(error.retry!)}
                >
                  <RotateCcw className="size-4" /> Повторить
                </Button>
              )}
            </div>
          )}

          <div className="text-sm text-muted-foreground">
            Позиций с разницей: <b className="text-foreground">{d.comparison.filter(hasDiff).length}</b> из{" "}
            {d.comparison.length}
          </div>
          <CompareTable rows={d.comparison} />

          {!outcome && mode === "finish_by_receipt" && (
            <div data-testid="discrepancy-pending" className="space-y-2 rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <div className="font-bold">Решено провести по принятому — документ не доведён</div>
              <div>
                {d.header.transfer_doc_number
                  ? `Перемещение № ${d.header.transfer_doc_number} создано, но не проведено в 1С.`
                  : KIND_TEXT[d.header.kind].notDone}{" "}
                Нажмите «Провести по принятому» ещё раз — портал доделает без второго документа. Или
                отмените перемещение — записанное в 1С будет отменено.
              </div>
              <Button className="h-12 w-full bg-emerald-600 hover:bg-emerald-700" disabled={actM.isPending} onClick={() => run("by_receipt")}>
                <CheckCircle2 className="size-4" /> Провести по принятому
              </Button>
              {cancelButton}
            </div>
          )}
          {!outcome && mode === "stuck_match" && (
            <div data-testid="discrepancy-stuck" className="space-y-2 rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <div className="font-bold">Получатель принял без расхождений — в 1С не доведено</div>
              <div>
                {d.header.transfer_doc_number
                  ? `Перемещение № ${d.header.transfer_doc_number} создано, но не проведено в 1С.`
                  : KIND_TEXT[d.header.kind].notDone}{" "}
                «Сбросить приёмку» — {KIND_TEXT[d.header.kind].resetWhat}, получатель примет заново.
                «Отменить» — перемещения не было, в 1С не останется ничего.
              </div>
              <Button className="h-12 w-full bg-amber-600 hover:bg-amber-700" disabled={actM.isPending} onClick={() => setConfirm("reset")}>
                <Undo2 className="size-4" /> Сбросить приёмку
              </Button>
              {cancelButton}
            </div>
          )}
          {!outcome && mode === "resolved" && (
            <div data-testid="discrepancy-resolved" className="rounded-lg border bg-slate-100 p-3 text-sm text-slate-800">
              <div className="font-bold">{resolvedText(d.header).title}</div>
              <div>{resolvedText(d.header).text}</div>
            </div>
          )}

          {!outcome && mode === "decide" && (
            <div className="grid gap-2 sm:grid-cols-3">
              <Button variant="outline" className="h-12" disabled={actM.isPending} onClick={() => setConfirm("recount")}>
                <Undo2 className="size-4" /> Пересчитать
              </Button>
              <Button className="h-12 bg-emerald-600 hover:bg-emerald-700" disabled={actM.isPending} onClick={() => setConfirm("by_receipt")}>
                <CheckCircle2 className="size-4" /> Провести по принятому
              </Button>
              <Button variant="outline" className="h-12 border-red-300 text-red-700" disabled={actM.isPending} onClick={() => setConfirm("cancel")}>
                <Ban className="size-4" /> Отменить
              </Button>
            </div>
          )}
          {(outcome || mode === "resolved") && (
            <Button variant="outline" className="h-12 w-full" onClick={goBack}>
              К списку расхождений
            </Button>
          )}
        </>
      )}

      {confirm && header && (
        <Dialog
          title={confirmView(confirm, header).title}
          body={confirmView(confirm, header).body}
          confirmLabel={confirmView(confirm, header).label}
          confirmBusy={actM.isPending}
          danger={confirmView(confirm, header).danger}
          onConfirm={() => run(confirm)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

function OutcomePanel({
  outcome,
  kind,
  retrying,
  onRetry,
}: {
  outcome: Outcome;
  kind: ReceiptKind;
  retrying: boolean;
  /** Документ создан, проведения нет — повтор доделывает его. */
  onRetry: () => void;
}): React.ReactElement {
  if (outcome.kind === "recount") {
    return (
      <div data-testid="discrepancy-outcome" className="rounded-lg border-2 border-sky-300 bg-sky-50 p-3 text-sm text-sky-900">
        <div className="font-bold">Возвращено получателю на пересчёт</div>
        <div>Приёмка снова в «Входящих» склада «{outcome.header.to_name || "—"}». В 1С ничего не записано.</div>
      </div>
    );
  }
  if (outcome.kind === "cancel") {
    return (
      <div data-testid="discrepancy-outcome" className="rounded-lg border-2 border-slate-300 bg-slate-100 p-3 text-sm text-slate-800">
        <div className="font-bold">Перемещение отменено</div>
        <div>В 1С по нему ничего не осталось. {KIND_TEXT[kind].afterCancel}</div>
      </div>
    );
  }
  if (outcome.kind === "reset") {
    return (
      <div data-testid="discrepancy-outcome" className="rounded-lg border-2 border-sky-300 bg-sky-50 p-3 text-sm text-sky-900">
        <div className="font-bold">Приёмка сброшена</div>
        <div>
          {kind === "free" ? "Документ в 1С отменён." : "Документ и правка заказа в 1С отменены."} Приёмка
          снова во «Входящих» склада «
          {outcome.header.to_name || "—"}» — получатель примет заново.
        </div>
      </div>
    );
  }
  const r = outcome.result;
  return (
    <div data-testid="discrepancy-outcome" className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
      <div className="font-bold">
        {r.posted
          ? `Перемещение № ${r.transfer_doc_number ?? "—"} проведено по принятому`
          : `Перемещение № ${r.transfer_doc_number ?? "—"} создано, но не проведено`}
      </div>
      <div>{KIND_TEXT[kind].byReceiptDone}</div>
      {!r.posted && (
        <Button className="mt-2 bg-red-700 hover:bg-red-800" disabled={retrying} onClick={onRetry}>
          <RotateCcw className="size-4" /> Повторить проведение
        </Button>
      )}
    </div>
  );
}
